/**
 * Elite Fade Designs - Floating "Book Now" Button (Mobile)
 *
 * Sticky bottom CTA, only visible on mobile.
 * Auto-hides on /booking, /dashboard, /efd-admin, /signin, /signup, /admin/*.
 * Self-contained: styles injected on first init.
 */

import { t } from '@i18n/index.js';

const HIDDEN_ROUTES = ['/booking', '/book', '/dashboard', '/efd-admin', '/signin', '/signup'];
const HIDDEN_PREFIXES = ['/admin'];

let btnEl = null;

/**
 * Initialize the floating button. Idempotent.
 * Mounts to <body>, listens for route + language changes.
 */
export function initFloatingBookBtn() {
  if (btnEl) return;

  ensureStyles();

  btnEl = document.createElement('a');
  btnEl.id = 'efd-fbb';
  btnEl.className = 'efd-fbb efd-fbb--hidden'; // start hidden, then evaluate
  btnEl.href = '/booking';

  render();
  attachEvents();

  document.body.appendChild(btnEl);

  // Force visibility eval right after the element is in the DOM,
  // not in the same synchronous frame as the .efd-fbb--hidden class above.
  // Otherwise the initial "hidden" state can persist on the first paint
  // for routes that should NOT hide it (race against router timing).
  requestAnimationFrame(() => updateVisibility());

  window.addEventListener('popstate', () => {
    render();
    updateVisibility();
  });

  window.addEventListener('languageChange', () => {
    render();
    updateVisibility();
  });
}

function render() {
  if (!btnEl) return;
  btnEl.setAttribute('aria-label', t('nav.bookNow'));
  btnEl.innerHTML = `
    <span class="efd-fbb-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    </span>
    <span class="efd-fbb-label">${t('nav.bookNow')}</span>
    <span class="efd-fbb-arrow" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
           stroke-linecap="round" stroke-linejoin="round" width="14" height="14">
        <path d="M5 12h14M13 5l7 7-7 7"/>
      </svg>
    </span>
  `;
}

function attachEvents() {
  btnEl.addEventListener('click', (e) => {
    e.preventDefault();
    if (currentPath() === '/booking') return;
    history.pushState(null, '', '/booking');
    window.dispatchEvent(new PopStateEvent('popstate'));
  });
}

function currentPath() {
  let p = window.location.pathname || '/';
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

function updateVisibility() {
  if (!btnEl) return;
  const path = currentPath();
  const shouldHide =
    HIDDEN_ROUTES.some(r => path === r || path.startsWith(r + '/')) ||
    HIDDEN_PREFIXES.some(p => path === p || path.startsWith(p + '/'));

  btnEl.classList.toggle('efd-fbb--hidden', shouldHide);
  // Keep <body> bottom padding only while the floating button occupies space.
  document.body.classList.toggle('efd-fbb-active', !shouldHide);
}

function ensureStyles() {
  if (document.getElementById('efd-fbb-styles')) return;

  const style = document.createElement('style');
  style.id = 'efd-fbb-styles';
  style.textContent = `
    .efd-fbb {
      display: none; /* hidden on desktop */
      position: fixed;
      left: 16px;
      right: 16px;
      bottom: 16px;
      bottom: max(16px, env(safe-area-inset-bottom));
      z-index: 100;

      align-items: center;
      justify-content: flex-start;
      gap: 12px;
      padding: 15px 22px;
      background: #FFFFFF;
      color: #0A0A0A;
      border-radius: 9999px;
      font-size: 1rem;
      font-weight: 600;
      letter-spacing: -0.01em;
      text-decoration: none;

      box-shadow:
        0 10px 30px rgba(0, 0, 0, 0.35),
        0 4px 10px rgba(0, 0, 0, 0.18);

      opacity: 1;
      transform: translateY(0);
      transition:
        opacity 0.35s ease,
        transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    }

    .efd-fbb:active {
      transform: scale(0.97);
    }

    .efd-fbb-icon,
    .efd-fbb-arrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .efd-fbb-label {
      flex: 1;
    }

    .efd-fbb-arrow {
      opacity: 0.55;
    }

    /* Hidden state - high-specificity selector so it always wins */
    .efd-fbb.efd-fbb--hidden {
      opacity: 0 !important;
      transform: translateY(140%) !important;
      pointer-events: none;
    }

    /* Show only on mobile / narrow tablets, only when route allows */
    @media (max-width: 720px) {
      .efd-fbb { display: flex; }
      body.efd-fbb-active { padding-bottom: 92px; }
    }
  `;
  document.head.appendChild(style);
}
